// ─── Step tree helpers ──────────────────────────────────────────────────────
// A step's address is a path like [2] (third top-level step) or [2, "then", 0]
// (first step in that step's IF YES lane). Lists are addressed the same way,
// minus the trailing index: [] is the main list, [2, "then"] is a lane.

export const samePath = (a, b) => Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((v, i) => v === b[i]);

export function getStepAt(steps, path) {
    const [idx, lane, ...rest] = path;
    const step = steps?.[idx];
    if (!step) return null;
    if (lane === undefined) return step;
    return getStepAt(step.branches?.[lane] || [], rest);
}

export function replaceStepAt(steps, path, updated) {
    const [idx, lane, ...rest] = path;
    return steps.map((s, i) => {
        if (i !== idx) return s;
        if (lane === undefined) return updated;
        return { ...s, branches: { ...(s.branches || {}), [lane]: replaceStepAt(s.branches?.[lane] || [], rest, updated) } };
    });
}

export function removeStepAt(steps, path) {
    const [idx, lane, ...rest] = path;
    if (lane === undefined) return steps.filter((_, i) => i !== idx);
    return steps.map((s, i) => (
        i !== idx ? s : { ...s, branches: { ...(s.branches || {}), [lane]: removeStepAt(s.branches?.[lane] || [], rest) } }
    ));
}

// listPath addresses the list to append to: [] = main list, [2,"then"] = a lane.
export function appendStepAt(steps, listPath, newStep) {
    if (!listPath.length) return [...steps, newStep];
    const [idx, lane, ...rest] = listPath;
    return steps.map((s, i) => (
        i !== idx ? s : { ...s, branches: { ...(s.branches || {}), [lane]: appendStepAt(s.branches?.[lane] || [], rest, newStep) } }
    ));
}

export function listLengthAt(steps, listPath) {
    if (!listPath.length) return steps.length;
    const [idx, lane, ...rest] = listPath;
    return listLengthAt(steps?.[idx]?.branches?.[lane] || [], rest);
}

// Which lanes a step shows on the canvas: fixed (if → then/else) or named by
// the step's own config (ai.decide → whatever branches the user listed).
export function lanesFor(step, meta) {
    const spec = meta?.branches;
    if (!spec) return [];
    if (spec.mode === "fixed") return spec.lanes || [];
    if (spec.mode === "dynamic") {
        const names = step.config?.[spec.from || "branches"];
        return (Array.isArray(names) ? names : []).map(n => ({ key: n, label: n }));
    }
    return [];
}

// Steps that run before `path`, innermost lane outward — including the parent
// If/Decide steps themselves, whose outputs are in scope inside their lanes.
export function priorStepsAlongPath(steps, path, metaMap) {
    const out = [];
    let list = steps;
    for (let k = 0; k < path.length; k += 2) {
        const idx = path[k];
        const lane = path[k + 1];
        for (let j = 0; j < idx; j++) out.push(list[j]);
        if (lane !== undefined) {
            if (list[idx]) out.push(list[idx]);
            list = list[idx]?.branches?.[lane] || [];
        }
    }
    return out.map(s => ({ name: s.name || s.type, type: s.type, outputs: metaMap[s.type]?.outputs || [] }));
}

// Steps that can't run inside a branch — they suspend the whole run, and a
// branch has nowhere to resume to. The executor rejects them too.
export const NO_NEST_TYPES = ["wait", "human_approval", "parallel"];

// Step positions come back as "0" for top-level steps and "2.then.0" for steps
// inside a branch. Show the first as #3, the second as #3 › if yes › #1.
export function formatStepIndex(raw) {
    const parts = String(raw ?? "").split(".");
    return parts
        .map(p => (/^\d+$/.test(p) ? `#${Number(p) + 1}` : (p === "then" ? "if yes" : p === "else" ? "if no" : p)))
        .join(" › ");
}
